import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Divider,
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Paper,
  IconButton,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  AutoAwesome as AutoAwesomeIcon,
  EventAvailable as EventAvailableIcon,
  Group as GroupIcon,
  FitnessCenter as FitnessCenterIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';

const SlotManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [slots, setSlots] = useState([]);
  const [occupancyStats, setOccupancyStats] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, slotId: null });
  
  // Dialog states
  const [openSlotDialog, setOpenSlotDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Form state
  const [slotForm, setSlotForm] = useState({
    name: '',
    description: '',
    startTime: null,
    endTime: null,
    maxCapacity: 10,
    daysOfWeek: [],
    validFrom: new Date(),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    slotType: 'general',
    instructor: '', // Will default to first trainer when rendering
    classType: 'zumba', // First class type as default
    equipment: '',
    price: 0,
    advanceBookingDays: 7,
    cancellationHours: 2
  });

  const daysOfWeek = [
    'monday', 'tuesday', 'wednesday', 'thursday', 
    'friday', 'saturday', 'sunday'
  ];

  const classTypes = [
    'zumba', 'yoga', 'pilates', 'dance', 'aerobics', 
    'spinning', 'crossfit', 'martial_arts', 'other'
  ];

  const dayLabels = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun'
  };

  useEffect(() => {
    fetchSlots();
    fetchTrainers();
    fetchOccupancyStats();
  }, []);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const response = await api.get('/slots/admin/slots');
      if (response.data.success) {
        setSlots(response.data.data);
      }
    } catch (error) {
      setError('Failed to fetch slots');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await api.get('/auth/trainers');
      if (response.data.success) {
        setTrainers(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch trainers:', error);
    }
  };

  const fetchOccupancyStats = async (date = new Date()) => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.get(`/slots/admin/occupancy?date=${dateStr}`);
      if (response.data.success) {
        setOccupancyStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch occupancy stats:', error);
    }
  };

  const handleCreateSlot = async () => {
    try {
      setLoading(true);
      setError('');

      const slotData = {
        ...slotForm,
        startTime: slotForm.startTime ? 
          `${slotForm.startTime.getHours().toString().padStart(2, '0')}:${slotForm.startTime.getMinutes().toString().padStart(2, '0')}` : '',
        endTime: slotForm.endTime ? 
          `${slotForm.endTime.getHours().toString().padStart(2, '0')}:${slotForm.endTime.getMinutes().toString().padStart(2, '0')}` : '',
        equipment: slotForm.equipment ? slotForm.equipment.split(',').map(e => e.trim()) : []
      };

      const response = editingSlot 
        ? await api.put(`/slots/admin/slots/${editingSlot._id}`, slotData)
        : await api.post('/slots/admin/slots', slotData);

      if (response.data.success) {
        setSuccess(editingSlot ? 'Slot updated successfully' : 'Slot created successfully');
        setOpenSlotDialog(false);
        resetForm();
        fetchSlots();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save slot');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSlot = (slot) => {
    setEditingSlot(slot);
    setSlotForm({
      name: slot.name,
      description: slot.description || '',
      startTime: new Date(`2000-01-01T${slot.startTime}:00`),
      endTime: new Date(`2000-01-01T${slot.endTime}:00`),
      maxCapacity: slot.maxCapacity,
      daysOfWeek: slot.daysOfWeek,
      validFrom: new Date(slot.validFrom),
      validUntil: new Date(slot.validUntil),
      slotType: slot.slotType,
      instructor: slot.instructor?._id || '',
      classType: slot.classType || '',
      equipment: slot.equipment?.join(', ') || '',
      price: slot.price || 0,
      advanceBookingDays: slot.advanceBookingDays,
      cancellationHours: slot.cancellationHours
    });
    setOpenSlotDialog(true);
  };

  const handleDeleteSlot = (slotId) => {
    setDeleteConfirm({ open: true, slotId });
  };

  const doDeleteSlot = async () => {
    const { slotId } = deleteConfirm;
    setDeleteConfirm({ open: false, slotId: null });
    try {
      setLoading(true);
      const response = await api.delete(`/slots/admin/slots/${slotId}`);
      if (response.data.success) {
        setSuccess('Slot deleted successfully');
        fetchSlots();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete slot');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingSlot(null);
    setSlotForm({
      name: '',
      description: '',
      startTime: null,
      endTime: null,
      maxCapacity: 10,
      daysOfWeek: [],
      validFrom: new Date(),
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      slotType: 'general',
      instructor: '',
      classType: 'zumba',
      equipment: '',
      price: 0,
      advanceBookingDays: 7,
      cancellationHours: 2
    });
  };

  const handleDayToggle = (day) => {
    setSlotForm(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const slotMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours = 0, minutes = 0] = timeString.split(':').map(Number);
    return (hours * 60) + minutes;
  };

  const readableSlotType = (slot) => {
    if (slot.slotType !== 'group_class') return 'Open Gym';
    if (!slot.classType) return 'Group Class';
    return slot.classType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getSlotPeriod = (slot) => {
    const minutes = slotMinutes(slot.startTime);
    if (minutes < 12 * 60) return 'Morning flow';
    if (minutes < 17 * 60) return 'Afternoon lift';
    return 'Evening peak';
  };

  const groupedSlots = slots.reduce((groups, slot) => {
    const period = getSlotPeriod(slot);
    if (!groups[period]) groups[period] = [];
    groups[period].push(slot);
    return groups;
  }, {});

  const slotPulse = {
    totalSlots: slots.length,
    weeklyCapacity: slots.reduce(
      (sum, slot) => sum + ((slot.maxCapacity || 0) * (slot.daysOfWeek?.length || 0)),
      0
    ),
    groupClasses: slots.filter(slot => slot.slotType === 'group_class').length,
    busiestWindow: Object.entries(groupedSlots)
      .sort((a, b) => b[1].length - a[1].length)[0]?.[0] || 'No slots'
  };

  const pulseCards = [
    {
      label: 'Bookable slots',
      value: slotPulse.totalSlots,
      note: 'Active templates',
      icon: <EventAvailableIcon />,
      color: '#174EA6'
    },
    {
      label: 'Weekly seats',
      value: slotPulse.weeklyCapacity,
      note: 'Potential capacity',
      icon: <GroupIcon />,
      color: '#0F766E'
    },
    {
      label: 'Class formats',
      value: slotPulse.groupClasses,
      note: 'Instructor led',
      icon: <AutoAwesomeIcon />,
      color: '#A16207'
    },
    {
      label: 'Demand window',
      value: slotPulse.busiestWindow,
      note: 'Most slot density',
      icon: <TimelineIcon />,
      color: '#7C3AED'
    }
  ];

  const occupancyTone = (percentage = 0) => {
    if (percentage >= 85) return { color: '#DC2626', label: 'Critical' };
    if (percentage >= 65) return { color: '#D97706', label: 'Busy' };
    if (percentage >= 35) return { color: '#2563EB', label: 'Balanced' };
    return { color: '#059669', label: 'Quiet' };
  };

  const getSlotTypeIcon = (type) => {
    return type === 'group_class' ? <GroupIcon /> : <FitnessCenterIcon />;
  };

  const getSlotTypeColor = (type) => {
    return type === 'group_class' ? 'secondary' : 'primary';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl">
        <PageHeader
          title="Slot Command Center"
          subtitle="Design the gym day as a live operating map: member flow, trainer coverage, capacity, and bookable experiences in one place."
          gradient="linear-gradient(90deg, #0F766E, #2DD4BF)"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Slot Management" />
            <Tab label="Occupancy Stats" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {pulseCards.map((card) => (
                <Grid item xs={12} sm={6} lg={3} key={card.label}>
                  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', boxShadow: (theme) => theme.palette.mode === 'dark' ? 'none' : '0 12px 28px rgba(15, 23, 42, 0.06)', bgcolor: 'background.paper' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: `${card.color}14`, color: card.color, width: 38, height: 38 }}>
                          {card.icon}
                        </Avatar>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                          {card.label}
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: 'text.primary' }}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {card.note}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>Experience lanes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Slots are grouped by member energy curve instead of a flat list.
                  </Typography>
                </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setOpenSlotDialog(true);
                }}
              >
                Create Slot
              </Button>
            </Box>

              {slots.length === 0 && !loading ? (
                <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                  <EventAvailableIcon sx={{ fontSize: 52, mb: 1, color: 'text.disabled' }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>No slots created yet</Typography>
                  <Typography variant="body2">Create your first recurring slot to start shaping member flow.</Typography>
                </Box>
              ) : (
                Object.entries(groupedSlots).map(([period, periodSlots]) => (
                  <Box key={period} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <AccessTimeIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'text.primary' }}>{period}</Typography>
                      <Chip size="small" label={`${periodSlots.length} slots`} sx={{ fontWeight: 700 }} />
                    </Box>
                    <Grid container spacing={2}>
                      {periodSlots
                        .sort((a, b) => slotMinutes(a.startTime) - slotMinutes(b.startTime))
                        .map((slot) => (
                          <Grid item xs={12} md={6} xl={4} key={slot._id}>
                            <Card sx={{ 
                              height: '100%', 
                              border: '1px solid',
                              borderColor: 'divider',
                              boxShadow: 'none', 
                              position: 'relative', 
                              overflow: 'hidden',
                              borderRadius: 3,
                              bgcolor: 'background.paper',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 12px 28px rgba(0,0,0,0.4)' : '0 12px 28px rgba(15, 23, 42, 0.08)',
                                borderColor: (theme) => theme.palette.mode === 'dark' ? 'primary.main' : '#CBD5E1'
                              }
                            }}>
                              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, bgcolor: slot.slotType === 'group_class' ? '#7C3AED' : '#2563EB' }} />
                              <CardContent sx={{ pl: 3.5, p: 2.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 950, color: 'text.primary', lineHeight: 1.2, mb: 0.5 }}>
                                      {slot.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                                      {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    icon={getSlotTypeIcon(slot.slotType)}
                                    label={readableSlotType(slot)}
                                    size="small"
                                    sx={{ 
                                      fontWeight: 800, 
                                      bgcolor: slot.slotType === 'group_class' ? (theme) => theme.palette.mode === 'dark' ? 'rgba(124, 58, 237, 0.15)' : '#F5F3FF' : (theme) => theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.15)' : '#EFF6FF',
                                      color: slot.slotType === 'group_class' ? (theme) => theme.palette.mode === 'dark' ? '#C4B5FD' : '#7C3AED' : (theme) => theme.palette.mode === 'dark' ? '#93C5FD' : '#2563EB',
                                      '& .MuiChip-icon': { color: 'inherit' }
                                    }}
                                  />
                                </Box>

                                {slot.description && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
                                    {slot.description}
                                  </Typography>
                                )}

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2.5, p: 1.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8FAFC', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Capacity</Typography>
                                    <Typography sx={{ fontWeight: 900, color: 'text.primary' }}>{slot.maxCapacity || 0} seats</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Window</Typography>
                                    <Typography sx={{ fontWeight: 900, color: 'text.primary' }}>{slot.advanceBookingDays || 0} days</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Trainer</Typography>
                                    <Typography sx={{ fontWeight: 900, color: 'text.primary' }} noWrap>{slot.instructor?.name || 'Open floor'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Price</Typography>
                                    <Typography sx={{ fontWeight: 900, color: 'text.primary' }}>{slot.price > 0 ? `$${slot.price}` : 'Free'}</Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                                    const active = (slot.daysOfWeek || []).includes(day);
                                    return (
                                      <Chip 
                                        key={day} 
                                        label={dayLabels[day]} 
                                        size="small" 
                                        sx={{ 
                                          bgcolor: active 
                                            ? (slot.slotType === 'group_class' 
                                                ? (theme) => theme.palette.mode === 'dark' ? 'rgba(124, 58, 237, 0.2)' : '#F5F3FF' 
                                                : (theme) => theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.2)' : '#EFF6FF')
                                            : (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F1F5F9',
                                          color: active 
                                            ? (slot.slotType === 'group_class' 
                                                ? (theme) => theme.palette.mode === 'dark' ? '#C4B5FD' : '#7C3AED'
                                                : (theme) => theme.palette.mode === 'dark' ? '#93C5FD' : '#2563EB')
                                            : 'text.secondary',
                                          border: active ? 'none' : '1px solid',
                                          borderColor: 'divider',
                                          fontWeight: active ? 800 : 600,
                                          fontSize: '0.7rem',
                                          height: 24
                                        }} 
                                      />
                                    );
                                  })}
                                </Box>

                                <Divider sx={{ mb: 1.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: 14 }} /> Cancel buffer: {slot.cancellationHours || 0}h
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Edit">
                                      <IconButton size="small" onClick={() => handleEditSlot(slot)} sx={{ color: 'text.secondary', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F8FAFC', '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#F1F5F9', color: 'text.primary' } }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton size="small" onClick={() => handleDeleteSlot(slot._id)} sx={{ color: '#FCA5A5', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2', '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#FEE2E2', color: '#EF4444' } }}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                ))
              )}
            </Paper>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Capacity radar</Typography>
                <Typography variant="body2" color="text.secondary">
                  Spot pressure, checked-in volume, and remaining capacity for the selected day.
                </Typography>
              </Box>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newDate) => {
                  setSelectedDate(newDate);
                  fetchOccupancyStats(newDate);
                }}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>

            {occupancyStats && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#174EA6' }}>
                          {occupancyStats.overallStats.totalSlots}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Slots
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#0F766E' }}>
                          {occupancyStats.overallStats.totalBookings}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Bookings
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#7C3AED' }}>
                          {occupancyStats.overallStats.totalCheckedIn}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Checked In
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#D97706' }}>
                          {occupancyStats.overallStats.overallOccupancyPercentage}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Occupancy Rate
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  {occupancyStats.slotStats.map((stat) => {
                    const tone = occupancyTone(stat.occupancyPercentage);
                    return (
                      <Grid item xs={12} md={6} key={stat.slot._id}>
                        <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                  {stat.slot.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatTime(stat.slot.startTime)} - {formatTime(stat.slot.endTime)}
                                </Typography>
                              </Box>
                              <Chip
                                label={tone.label}
                                size="small"
                                sx={{ bgcolor: `${tone.color}14`, color: tone.color, fontWeight: 900 }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(stat.occupancyPercentage || 0, 100)}
                                sx={{
                                  flex: 1,
                                  height: 10,
                                  borderRadius: 999,
                                  bgcolor: 'action.hover',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: tone.color,
                                    borderRadius: 999
                                  }
                                }}
                              />
                              <Typography sx={{ fontWeight: 900, minWidth: 44, textAlign: 'right' }}>
                                {stat.occupancyPercentage}%
                              </Typography>
                            </Box>

                            <Grid container spacing={1.5} sx={{ mt: 1 }}>
                              {[
                                ['Capacity', stat.slot.maxCapacity],
                                ['Booked', stat.totalBookings],
                                ['Checked in', stat.checkedInCount],
                                ['Available', stat.availableSpots]
                              ].map(([label, value]) => (
                                <Grid item xs={6} sm={3} key={label}>
                                  <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F8FAFC' }}>
                                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                                    <Typography sx={{ fontWeight: 900 }}>{value}</Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Box>
        )}

        {/* Create/Edit Slot Dialog */}
        <Dialog open={openSlotDialog} onClose={() => setOpenSlotDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingSlot ? 'Edit Slot' : 'Create New Slot'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Slot Name"
                  value={slotForm.name}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Slot Type</InputLabel>
                  <Select
                    value={slotForm.slotType}
                    onChange={(e) => setSlotForm(prev => ({ ...prev, slotType: e.target.value }))}
                    label="Slot Type"
                  >
                    <MenuItem value="general">General Gym</MenuItem>
                    <MenuItem value="group_class">Group Class</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={slotForm.description}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="Start Time"
                  value={slotForm.startTime}
                  onChange={(newValue) => setSlotForm(prev => ({ ...prev, startTime: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="End Time"
                  value={slotForm.endTime}
                  onChange={(newValue) => setSlotForm(prev => ({ ...prev, endTime: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Capacity"
                  type="number"
                  value={slotForm.maxCapacity}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, maxCapacity: parseInt(e.target.value) }))}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price (optional)"
                  type="number"
                  value={slotForm.price}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {slotForm.slotType === 'group_class' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Instructor</InputLabel>
                      <Select
                        value={slotForm.instructor || (trainers.length > 0 ? trainers[0]._id : '')}
                        onChange={(e) => setSlotForm(prev => ({ ...prev, instructor: e.target.value }))}
                        label="Instructor"
                      >
                        {trainers.map((trainer) => (
                          <MenuItem key={trainer._id} value={trainer._id}>
                            {trainer.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Class Type</InputLabel>
                      <Select
                        value={slotForm.classType}
                        onChange={(e) => setSlotForm(prev => ({ ...prev, classType: e.target.value }))}
                        label="Class Type"
                      >
                        {classTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Equipment (comma-separated)"
                      value={slotForm.equipment}
                      onChange={(e) => setSlotForm(prev => ({ ...prev, equipment: e.target.value }))}
                      placeholder="e.g., Yoga mats, Dumbbells, Resistance bands"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Days of Week
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {daysOfWeek.map((day) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={slotForm.daysOfWeek.includes(day)}
                          onChange={() => handleDayToggle(day)}
                        />
                      }
                      label={day.charAt(0).toUpperCase() + day.slice(1)}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Valid From"
                  value={slotForm.validFrom}
                  onChange={(newValue) => setSlotForm(prev => ({ ...prev, validFrom: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Valid Until"
                  value={slotForm.validUntil}
                  onChange={(newValue) => setSlotForm(prev => ({ ...prev, validUntil: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Advance Booking Days"
                  type="number"
                  value={slotForm.advanceBookingDays}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, advanceBookingDays: parseInt(e.target.value) }))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cancellation Hours"
                  type="number"
                  value={slotForm.cancellationHours}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, cancellationHours: parseInt(e.target.value) }))}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSlotDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateSlot} 
              variant="contained" 
              disabled={loading || !slotForm.name || !slotForm.startTime || !slotForm.endTime || slotForm.daysOfWeek.length === 0}
            >
              {loading ? 'Saving...' : (editingSlot ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Slot"
        message="This will permanently delete this slot and all its bookings."
        confirmLabel="Delete"
        onConfirm={doDeleteSlot}
        onCancel={() => setDeleteConfirm({ open: false, slotId: null })}
      />
    </LocalizationProvider>
  );
};

export default SlotManagement;
